import React, { useState, useRef } from 'react';
import { FiUploadCloud, FiX, FiImage } from 'react-icons/fi';
import { toast } from 'react-toastify';
import uploadService from '../../services/upload.service';

const ImageUpload = ({ images = [], onChange, max = 8 }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    if (images.length + files.length > max) {
      toast.warning(`Maximum ${max} images allowed`);
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append('images', file));

      const { data } = await uploadService.uploadMultiple(files);

      const newImages = data.data.map((file, index) => ({
        url: file.url,
        alt: file.originalName,
        isPrimary: images.length === 0 && index === 0,
      }));

      onChange([...images, ...newImages]);
      toast.success(`${files.length} image(s) uploaded`);
    } catch (error) {
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemove = (index) => {
    const updated = images.filter((_, i) => i !== index);
    // If removed image was primary, make first one primary
    if (images[index]?.isPrimary && updated.length > 0) {
      updated[0].isPrimary = true;
    }
    onChange(updated);
  };

  const handleSetPrimary = (index) => {
    const updated = images.map((img, i) => ({
      ...img,
      isPrimary: i === index,
    }));
    onChange(updated);
  };

  return (
    <div>
      <div className="d-flex flex-wrap gap-2 mb-2">
        {images.map((img, index) => (
          <div
            key={index}
            className={`position-relative border rounded ${img.isPrimary ? 'border-primary border-2' : 'border-light'}`}
            style={{ width: '100px', height: '100px' }}
          >
            <img
              src={img.url}
              alt={img.alt || ''}
              className="w-100 h-100 rounded"
              style={{ objectFit: 'cover', cursor: 'pointer' }}
              onClick={() => handleSetPrimary(index)}
              title={img.isPrimary ? 'Primary image' : 'Click to set as primary'}
            />
            <button
              type="button"
              className="btn btn-sm btn-danger position-absolute top-0 end-0 p-0 d-flex align-items-center justify-content-center"
              style={{ width: '20px', height: '20px', borderRadius: '50%', transform: 'translate(30%, -30%)' }}
              onClick={() => handleRemove(index)}
            >
              <FiX size={12} />
            </button>
            {img.isPrimary && (
              <span
                className="position-absolute bottom-0 start-0 badge bg-primary"
                style={{ fontSize: '9px', borderRadius: '0 4px 0 4px' }}
              >
                Primary
              </span>
            )}
          </div>
        ))}

        {images.length < max && (
          <div
            className="border border-dashed rounded d-flex flex-column align-items-center justify-content-center text-muted"
            style={{ width: '100px', height: '100px', cursor: 'pointer', borderStyle: 'dashed' }}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? (
              <div className="spinner-border spinner-border-sm" />
            ) : (
              <>
                <FiUploadCloud size={20} />
                <small style={{ fontSize: '10px' }}>Upload</small>
              </>
            )}
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*"
        className="d-none"
        onChange={handleFileSelect}
      />

      <small className="text-muted">
        <FiImage size={12} className="me-1" />
        {images.length}/{max} images · Click an image to set as primary · Max 5MB each
      </small>
    </div>
  );
};

export default ImageUpload;
